import { c } from "react/compiler-runtime";
import isPropValid from "@emotion/is-prop-valid";
import { Children, createContext, memo, use, useContext, useDebugValue, useInsertionEffect, useRef, useState, useSyncExternalStore } from "react";
import shallowequal from "shallowequal";
import * as stylis$1 from "stylis";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import unitless from "@emotion/unitless";

//#region src/constants.ts
const SC_ATTR = typeof process !== "undefined" && typeof process.env !== "undefined" && (process.env.REACT_APP_SC_ATTR || process.env.SC_ATTR || process.env.CSS_IN_JS_ATTR) || "data-styled";
const SC_ATTR_ACTIVE = "active";
const SC_ATTR_VERSION = "data-styled-version";
const SC_VERSION = "6.1.25";
const SPLITTER = "/*!sc*/\n";
const IS_BROWSER = typeof window !== "undefined" && typeof document !== "undefined";
const DISABLE_SPEEDY = false;
const STATIC_EXECUTION_CONTEXT = {};

//#endregion
//#region src/utils/checkDynamicCreation.ts
const invalidHookCallRe = /invalid hook call/i;
const seen = /* @__PURE__ */ new Set();
const checkDynamicCreation = (displayName, componentId) => {
	if (process.env.NODE_ENV !== "production") {
		const parsedIdString = componentId ? ` with the id of "${componentId}"` : "";
		const message = `The component ${displayName}${parsedIdString} has been created dynamically.\nYou may see this warning because you've called styled inside another component.
To resolve this only create new StyledComponents outside of any render method and function component.
See https://styled-components.com/docs/basics#define-styled-components-outside-of-the-render-method for more info.
`;
		const originalConsoleError = console.error;
		try {
			let didNotCallInvalidHook = true;
			console.error = (consoleErrorMessage, ...consoleErrorArgs) => {
				if (invalidHookCallRe.test(consoleErrorMessage)) {
					didNotCallInvalidHook = false;
					seen.delete(message);
				} else originalConsoleError(consoleErrorMessage, ...consoleErrorArgs);
			};
			useRef();
			if (didNotCallInvalidHook && !seen.has(message)) {
				console.warn(message);
				seen.add(message);
			}
		} catch (error) {
			if (invalidHookCallRe.test(error.message)) seen.delete(message);
		} finally {
			console.error = originalConsoleError;
		}
	}
};

//#endregion
//#region src/utils/createWarnTooManyClasses.ts
const LIMIT = 200;
var createWarnTooManyClasses_default = (displayName, componentId) => {
	let generatedClasses = {};
	let warningSeen = false;
	return (className) => {
		if (!warningSeen) {
			generatedClasses[className] = true;
			if (Object.keys(generatedClasses).length >= LIMIT) {
				const parsedIdString = componentId ? ` with the id of "${componentId}"` : "";
				console.warn(`Over ${LIMIT} classes were generated for component ${displayName}${parsedIdString}.\nConsider using the attrs method, together with a style object for frequently changed styles.
Example:
  const Component = styled.div.attrs(props => ({
    style: {
      background: props.background,
    },
  }))\`width: 100%;\`

  <Component />`);
				warningSeen = true;
				generatedClasses = {};
			}
		}
	};
};

//#endregion
//#region src/utils/empties.ts
const EMPTY_ARRAY = Object.freeze([]);
const EMPTY_OBJECT = Object.freeze({});

//#endregion
//#region src/utils/determineTheme.ts
function determineTheme(props, providedTheme, defaultProps = EMPTY_OBJECT) {
	return props.theme !== defaultProps.theme && props.theme || providedTheme || defaultProps.theme;
}

//#endregion
//#region src/utils/domElements.ts
const elements = [
	"a",
	"abbr",
	"address",
	"area",
	"article",
	"aside",
	"audio",
	"b",
	"base",
	"bdi",
	"bdo",
	"big",
	"blockquote",
	"body",
	"br",
	"button",
	"canvas",
	"caption",
	"cite",
	"code",
	"col",
	"colgroup",
	"data",
	"datalist",
	"dd",
	"del",
	"details",
	"dfn",
	"dialog",
	"div",
	"dl",
	"dt",
	"em",
	"embed",
	"fieldset",
	"figcaption",
	"figure",
	"footer",
	"form",
	"h1",
	"h2",
	"h3",
	"h4",
	"h5",
	"h6",
	"header",
	"hgroup",
	"hr",
	"html",
	"i",
	"iframe",
	"img",
	"input",
	"ins",
	"kbd",
	"keygen",
	"label",
	"legend",
	"li",
	"link",
	"main",
	"map",
	"mark",
	"menu",
	"menuitem",
	"meta",
	"meter",
	"nav",
	"noscript",
	"object",
	"ol",
	"optgroup",
	"option",
	"output",
	"p",
	"param",
	"picture",
	"pre",
	"progress",
	"q",
	"rp",
	"rt",
	"ruby",
	"s",
	"samp",
	"script",
	"section",
	"select",
	"small",
	"source",
	"span",
	"strong",
	"style",
	"sub",
	"summary",
	"sup",
	"table",
	"tbody",
	"td",
	"textarea",
	"tfoot",
	"th",
	"thead",
	"time",
	"tr",
	"track",
	"u",
	"ul",
	"use",
	"var",
	"video",
	"wbr",
	"circle",
	"clipPath",
	"defs",
	"ellipse",
	"foreignObject",
	"g",
	"image",
	"line",
	"linearGradient",
	"marker",
	"mask",
	"path",
	"pattern",
	"polygon",
	"polyline",
	"radialGradient",
	"rect",
	"stop",
	"svg",
	"text",
	"tspan"
];
var domElements_default = new Set(elements);

//#endregion
//#region src/utils/escape.ts
const escapeRegex = /[!"#$%&'()*+,./:;<=>?@[\\\]^`{|}~-]+/g;
const dashesAtEnds = /(^-|-$)/g;
/**
* TODO: Explore using CSS.escape when it becomes more available
* in evergreen browsers.
*/
function escape(str) {
	return str.replace(escapeRegex, "-").replace(dashesAtEnds, "");
}

//#endregion
//#region src/utils/generateAlphabeticName.ts
const AD_REPLACER_R = /(a)(d)/gi;
const charsLength = 52;
const getAlphabeticChar = (code) => String.fromCharCode(code + (code > 25 ? 39 : 97));
function generateAlphabeticName(code) {
	let name = "";
	let x;
	for (x = Math.abs(code); x > charsLength; x = x / charsLength | 0) name = getAlphabeticChar(x % charsLength) + name;
	return (getAlphabeticChar(x % charsLength) + name).replace(AD_REPLACER_R, "$1-$2");
}

//#endregion
//#region src/utils/hash.ts
const SEED$1 = 5381;
const phash = (h, x) => {
	let i = x.length;
	while (i) h = Math.imul(h, 33) ^ x.charCodeAt(--i);
	return h;
};
const hash$1 = (x) => {
	return phash(SEED$1, x);
};

//#endregion
//#region src/utils/generateComponentId.ts
function generateComponentId(str) {
	return generateAlphabeticName(hash$1(str) >>> 0);
}

//#endregion
//#region src/utils/getComponentName.ts
function getComponentName(target) {
	return (process.env.NODE_ENV !== "production" ? typeof target === "string" && target : false) || target.displayName || target.name || "Component";
}

//#endregion
//#region src/utils/isTag.ts
function isTag(target) {
	return typeof target === "string" && (process.env.NODE_ENV !== "production" ? target.charAt(0) === target.charAt(0).toLowerCase() : true);
}

//#endregion
//#region src/utils/generateDisplayName.ts
function generateDisplayName(target) {
	return isTag(target) ? `styled.${target}` : `Styled(${getComponentName(target)})`;
}

//#endregion
//#region src/utils/hoist.ts
const hasSymbol = typeof Symbol === "function" && Symbol.for;
const REACT_MEMO_TYPE = hasSymbol ? Symbol.for("react.memo") : 60115;
const REACT_FORWARD_REF_TYPE = hasSymbol ? Symbol.for("react.forward_ref") : 60112;
/**
* Adapted from hoist-non-react-statics to avoid the react-is dependency.
*/
const REACT_STATICS = {
	childContextTypes: true,
	contextType: true,
	contextTypes: true,
	defaultProps: true,
	displayName: true,
	getDefaultProps: true,
	getDerivedStateFromError: true,
	getDerivedStateFromProps: true,
	mixins: true,
	propTypes: true,
	type: true
};
const KNOWN_STATICS = {
	name: true,
	length: true,
	prototype: true,
	caller: true,
	callee: true,
	arguments: true,
	arity: true
};
const FORWARD_REF_STATICS = {
	$$typeof: true,
	render: true,
	defaultProps: true,
	displayName: true,
	propTypes: true
};
const MEMO_STATICS = {
	$$typeof: true,
	compare: true,
	defaultProps: true,
	displayName: true,
	propTypes: true,
	type: true
};
const TYPE_STATICS = {
	[REACT_FORWARD_REF_TYPE]: FORWARD_REF_STATICS,
	[REACT_MEMO_TYPE]: MEMO_STATICS
};
function isMemo(object) {
	return ("type" in object && object.type.$$typeof) === REACT_MEMO_TYPE;
}
function getStatics(component) {
	if (isMemo(component)) return MEMO_STATICS;
	return "$$typeof" in component ? TYPE_STATICS[component["$$typeof"]] : REACT_STATICS;
}
const defineProperty = Object.defineProperty;
const getOwnPropertyNames = Object.getOwnPropertyNames;
const getOwnPropertySymbols = Object.getOwnPropertySymbols;
const getOwnPropertyDescriptor = Object.getOwnPropertyDescriptor;
const getPrototypeOf = Object.getPrototypeOf;
const objectPrototype = Object.prototype;
function hoistNonReactStatics(targetComponent, sourceComponent, excludelist) {
	if (typeof sourceComponent !== "string") {
		if (objectPrototype) {
			const inheritedComponent = getPrototypeOf(sourceComponent);
			if (inheritedComponent && inheritedComponent !== objectPrototype) hoistNonReactStatics(targetComponent, inheritedComponent, excludelist);
		}
		let keys = getOwnPropertyNames(sourceComponent);
		if (getOwnPropertySymbols) keys = keys.concat(getOwnPropertySymbols(sourceComponent));
		const targetStatics = getStatics(targetComponent);
		const sourceStatics = getStatics(sourceComponent);
		for (let i = 0; i < keys.length; ++i) {
			const key = keys[i];
			if (!(key in KNOWN_STATICS) && !(excludelist && excludelist[key]) && !(sourceStatics && key in sourceStatics) && !(targetStatics && key in targetStatics)) {
				const descriptor = getOwnPropertyDescriptor(sourceComponent, key);
				try {
					defineProperty(targetComponent, key, descriptor);
				} catch {}
			}
		}
	}
	return targetComponent;
}

//#endregion
//#region src/utils/isFunction.ts
function isFunction(test) {
	return typeof test === "function";
}

//#endregion
//#region src/utils/isStyledComponent.ts
function isStyledComponent(target) {
	return typeof target === "object" && "styledComponentId" in target;
}

//#endregion
//#region src/utils/joinStrings.ts
/**
* Convenience function for joining strings to form className chains
*/
function joinStrings(a, b) {
	return a && b ? `${a} ${b}` : a || b || "";
}
function joinStringArray(arr, sep) {
	if (arr.length === 0) return "";
	let result = arr[0];
	for (let i = 1; i < arr.length; i++) result += sep ? sep + arr[i] : arr[i];
	return result;
}

//#endregion
//#region src/utils/isPlainObject.ts
function isPlainObject(x) {
	return x !== null && typeof x === "object" && x.constructor.name === Object.name && !("props" in x && x.$$typeof);
}

//#endregion
//#region src/utils/mixinDeep.ts
function mixinRecursively(target, source, forceMerge = false) {
	if (!forceMerge && !isPlainObject(target) && !Array.isArray(target)) return source;
	if (Array.isArray(source)) for (let key = 0; key < source.length; key++) target[key] = mixinRecursively(target[key], source[key]);
	else if (isPlainObject(source)) for (const key in source) target[key] = mixinRecursively(target[key], source[key]);
	return target;
}
/**
* Arrays & POJOs merged recursively, other objects and value types are overridden
* If target is not a POJO or an Array, it will get source properties injected via shallow merge
* Source objects applied left to right.  Mutates & returns target.  Similar to lodash merge.
*/
function mixinDeep(target, ...sources) {
	for (const source of sources) mixinRecursively(target, source, true);
	return target;
}

//#endregion
//#region src/utils/errors.ts
var errors_default = {
	"1": "Cannot create styled-component for component: %s.\n\n",
	"2": "Can't collect styles once you've consumed a `ServerStyleSheet`'s styles! `ServerStyleSheet` is a one off instance for each server-side render cycle.\n\n- Are you trying to reuse it across renders?\n- Are you accidentally calling collectStyles twice?\n\n",
	"3": "Streaming SSR is only supported in a Node.js environment; Please do not try to call this method in the browser.\n\n",
	"4": "The `StyleSheetManager` expects a valid target or sheet prop!\n\n- Does this error occur on the client and is your target falsy?\n- Does this error occur on the server and is the sheet falsy?\n\n",
	"5": "The clone method cannot be used on the client!\n\n- Are you running in a client-like environment on the server?\n- Are you trying to run SSR on the client?\n\n",
	"6": "Trying to insert a new style tag, but the given Node is unmounted!\n\n- Are you using a custom target that isn't mounted?\n- Does your document not have a valid head element?\n- Have you accidentally removed a style tag manually?\n\n",
	"7": "ThemeProvider: Please return an object from your \"theme\" prop function, e.g.\n\n```js\ntheme={() => ({})}\n```\n\n",
	"8": "ThemeProvider: Please make your \"theme\" prop an object.\n\n",
	"9": "Missing document `<head>`\n\n",
	"10": "Cannot find a StyleSheet instance. Usually this happens if there are multiple copies of styled-components loaded at once. Check out this issue for how to troubleshoot and fix the common cases where this situation can happen: https://github.com/styled-components/styled-components/issues/1941#issuecomment-417862021\n\n",
	"11": "_This error was replaced with a dev-time warning, it will be deleted for v4 final._ [createGlobalStyle] received children which will not be rendered. Please use the component without passing children elements.\n\n",
	"12": "It seems you are interpolating a keyframe declaration (%s) into an untagged string. This was supported in styled-components v3, but is not longer supported in v4 as keyframes are now injected on-demand. Please wrap your string in the css\\`\\` helper which ensures the styles are injected correctly. See https://www.styled-components.com/docs/api#css\n\n",
	"13": "%s is not a styled component and cannot be referred to via component selector. See https://www.styled-components.com/docs/advanced#referring-to-other-components for more details.\n\n",
	"14": "ThemeProvider: \"theme\" prop is required.\n\n",
	"15": "A stylis plugin has been supplied that is not named. We need a name for each plugin to be able to prevent styling collisions between different stylis configurations within the same app. Before you pass your plugin to `<StyleSheetManager stylisPlugins={[]}>`, please make sure each plugin is uniquely-named, e.g.\n\n```js\nObject.defineProperty(importedPlugin, 'name', { value: 'some-unique-name' });\n```\n\n",
	"16": "Reached the limit of how many styled components may be created at group %s.\nYou may only create up to 1,073,741,824 components. If you're creating components dynamically,\nas for instance in your render method then you may be running into this limitation.\n\n",
	"17": "CSSStyleSheet could not be found on HTMLStyleElement.\nHas styled-components' style tag been unmounted or altered by another script?\n",
	"18": "ThemeProvider: Please make sure your useTheme hook is within a `<ThemeProvider>`"
};

//#endregion
//#region src/utils/error.ts
const ERRORS = process.env.NODE_ENV !== "production" ? errors_default : {};
/**
* super basic version of sprintf
*/
function format(...args) {
	let a = args[0];
	const b = [];
	for (let c$1 = 1, len = args.length; c$1 < len; c$1 += 1) b.push(args[c$1]);
	b.forEach((d) => {
		a = a.replace(/%[a-z]/, d);
	});
	return a;
}
/**
* Create an error file out of errors.md for development and a simple web link to the full errors
* in production mode.
*/
function throwStyledComponentsError(code, ...interpolations) {
	if (process.env.NODE_ENV === "production") return /* @__PURE__ */ new Error(`An error occurred. See https://github.com/styled-components/styled-components/blob/main/packages/styled-components/src/utils/errors.md#${code} for more information.${interpolations.length > 0 ? ` Args: ${interpolations.join(", ")}` : ""}`);
	else return new Error(format(ERRORS[code], ...interpolations).trim());
}

//#endregion
//#region src/sheet/GroupedTag.ts
/** Create a GroupedTag with an underlying Tag implementation */
const makeGroupedTag = (tag) => {
	return new DefaultGroupedTag(tag);
};
const BASE_SIZE = 512;
var DefaultGroupedTag = class {
	groupSizes;
	length;
	tag;
	constructor(tag) {
		this.groupSizes = new Uint32Array(BASE_SIZE);
		this.length = BASE_SIZE;
		this.tag = tag;
	}
	indexOfGroup(group) {
		let index = 0;
		for (let i = 0; i < group; i++) index += this.groupSizes[i];
		return index;
	}
	insertRules(group, rules) {
		if (group >= this.groupSizes.length) {
			const oldBuffer = this.groupSizes;
			const oldSize = oldBuffer.length;
			let newSize = oldSize;
			while (group >= newSize) {
				newSize <<= 1;
				if (newSize < 0) throw throwStyledComponentsError(16, `${group}`);
			}
			this.groupSizes = new Uint32Array(newSize);
			this.groupSizes.set(oldBuffer);
			this.length = newSize;
			for (let i = oldSize; i < newSize; i++) this.groupSizes[i] = 0;
		}
		let ruleIndex = this.indexOfGroup(group + 1);
		for (let i = 0, l = rules.length; i < l; i++) if (this.tag.insertRule(ruleIndex, rules[i])) {
			this.groupSizes[group]++;
			ruleIndex++;
		}
	}
	clearGroup(group) {
		if (group < this.length) {
			const length = this.groupSizes[group];
			const startIndex = this.indexOfGroup(group);
			const endIndex = startIndex + length;
			this.groupSizes[group] = 0;
			for (let i = startIndex; i < endIndex; i++) this.tag.deleteRule(startIndex);
		}
	}
	getGroup(group) {
		let css$1 = "";
		if (group >= this.length || this.groupSizes[group] === 0) return css$1;
		const length = this.groupSizes[group];
		const startIndex = this.indexOfGroup(group);
		const endIndex = startIndex + length;
		for (let i = startIndex; i < endIndex; i++) css$1 += `${this.tag.getRule(i)}${SPLITTER}`;
		return css$1;
	}
};

//#endregion
//#region src/sheet/GroupIDAllocator.ts
const MAX_SMI = 1 << 30;
let groupIDRegister = /* @__PURE__ */ new Map();
let reverseRegister = /* @__PURE__ */ new Map();
let nextFreeGroup = 1;
const getGroupForId = (id) => {
	if (groupIDRegister.has(id)) return groupIDRegister.get(id);
	while (reverseRegister.has(nextFreeGroup)) nextFreeGroup++;
	const group = nextFreeGroup++;
	if (process.env.NODE_ENV !== "production" && ((group | 0) < 0 || group > MAX_SMI)) throw throwStyledComponentsError(16, `${group}`);
	groupIDRegister.set(id, group);
	reverseRegister.set(group, id);
	return group;
};
const getIdForGroup = (group) => {
	return reverseRegister.get(group);
};

//#endregion
//#region src/utils/nonce.ts
function getNonce() {
	return typeof __webpack_nonce__ !== "undefined" ? __webpack_nonce__ : null;
}

//#endregion
//#region src/sheet/dom.ts
/** Find last style element if any inside target */
const findLastStyleTag = (target) => {
	const arr = Array.from(target.querySelectorAll(`style[${SC_ATTR}]`));
	return arr[arr.length - 1];
};
/** Create a style element inside `target` or <head> after the last */
const makeStyleTag = (target) => {
	const head = document.head;
	const parent = target || head;
	const style = document.createElement("style");
	const prevStyle = findLastStyleTag(parent);
	const nextSibling = prevStyle !== void 0 ? prevStyle.nextSibling : null;
	style.setAttribute(SC_ATTR, SC_ATTR_ACTIVE);
	style.setAttribute(SC_ATTR_VERSION, SC_VERSION);
	const nonce = getNonce();
	if (nonce) style.setAttribute("nonce", nonce);
	parent.insertBefore(style, nextSibling);
	return style;
};
/** Get the CSSStyleSheet instance for a given style element */
const getSheet = (tag) => {
	if (tag.sheet) return tag.sheet;
	const { styleSheets } = document;
	for (let i = 0, l = styleSheets.length; i < l; i++) {
		const sheet = styleSheets[i];
		if (sheet.ownerNode === tag) return sheet;
	}
	throw throwStyledComponentsError(17);
};

//#endregion
//#region src/sheet/Tag.ts
/** Create a CSSStyleSheet-like tag depending on the environment */
const makeTag = ({ isServer, useCSSOMInjection, target }) => {
	if (isServer) return new VirtualTag(target);
	else if (useCSSOMInjection) return new CSSOMTag(target);
	else return new TextTag(target);
};
var CSSOMTag = class {
	element;
	sheet;
	length;
	constructor(target) {
		this.element = makeStyleTag(target);
		this.element.appendChild(document.createTextNode(""));
		this.sheet = getSheet(this.element);
		this.length = 0;
	}
	insertRule(index, rule) {
		try {
			this.sheet.insertRule(rule, index);
			this.length++;
			return true;
		} catch {
			return false;
		}
	}
	deleteRule(index) {
		this.sheet.deleteRule(index);
		this.length--;
	}
	getRule(index) {
		const rule = this.sheet.cssRules[index];
		if (rule && rule.cssText) return rule.cssText;
		else return "";
	}
};
/** A Tag that emulates the CSSStyleSheet API but uses text nodes */
var TextTag = class {
	element;
	nodes;
	length;
	constructor(target) {
		this.element = makeStyleTag(target);
		this.nodes = this.element.childNodes;
		this.length = 0;
	}
	insertRule(index, rule) {
		if (index <= this.length && index >= 0) {
			const node = document.createTextNode(rule);
			const refNode = this.nodes[index];
			this.element.insertBefore(node, refNode || null);
			this.length++;
			return true;
		} else return false;
	}
	deleteRule(index) {
		this.element.removeChild(this.nodes[index]);
		this.length--;
	}
	getRule(index) {
		if (index < this.length) return this.nodes[index].textContent;
		else return "";
	}
};
/** A completely virtual (server-side) Tag that doesn't manipulate the DOM */
var VirtualTag = class {
	rules;
	length;
	constructor(_target) {
		this.rules = [];
		this.length = 0;
	}
	insertRule(index, rule) {
		if (index <= this.length) {
			this.rules.splice(index, 0, rule);
			this.length++;
			return true;
		} else return false;
	}
	deleteRule(index) {
		this.rules.splice(index, 1);
		this.length--;
	}
	getRule(index) {
		if (index < this.length) return this.rules[index];
		else return "";
	}
};

//#endregion
//#region src/sheet/Sheet.ts
const defaultOptions = {
	isServer: !IS_BROWSER,
	useCSSOMInjection: !DISABLE_SPEEDY
};
/** Contains the main stylesheet logic for stringification and caching */
var StyleSheet = class StyleSheet {
	gs;
	names;
	options;
	server;
	tag;
	/** Register a group ID to give it an index */
	static registerId(id) {
		return getGroupForId(id);
	}
	constructor(options = EMPTY_OBJECT, globalStyles = {}, names) {
		this.options = {
			...defaultOptions,
			...options
		};
		this.gs = globalStyles;
		this.names = new Map(names);
		this.server = !!options.isServer;
	}
	reconstructWithOptions(options, withNames = true) {
		return new StyleSheet({
			...this.options,
			...options
		}, this.gs, withNames && this.names || void 0);
	}
	allocateGSInstance(id) {
		return this.gs[id] = (this.gs[id] || 0) + 1;
	}
	/** Lazily initialises a GroupedTag for when it's actually needed */
	getTag() {
		return this.tag || (this.tag = makeGroupedTag(makeTag(this.options)));
	}
	/** Check whether a name is known for caching */
	hasNameForId(id, name) {
		return this.names.has(id) && this.names.get(id).has(name);
	}
	/** Mark a group's name as known for caching */
	registerName(id, name) {
		getGroupForId(id);
		if (!this.names.has(id)) {
			const groupNames = /* @__PURE__ */ new Set();
			groupNames.add(name);
			this.names.set(id, groupNames);
		} else this.names.get(id).add(name);
	}
	/** Insert new rules which also marks the name as known */
	insertRules(id, name, rules) {
		this.registerName(id, name);
		this.getTag().insertRules(getGroupForId(id), rules);
	}
	/** Clears all cached names for a given group ID */
	clearNames(id) {
		if (this.names.has(id)) this.names.get(id).clear();
	}
	/** Clears all rules for a given group ID */
	clearRules(id) {
		this.getTag().clearGroup(getGroupForId(id));
		this.clearNames(id);
	}
	/** Clears the entire tag which deletes all rules but not its names */
	clearTag() {
		this.tag = void 0;
	}
};

//#endregion
//#region src/utils/stylis.ts
const AMP_REGEX = /&/g;
const COMMENT_REGEX = /^\s*\/\/.*$/gm;
/**
* Takes an element and recurses through it's rules added the namespace to the start of each selector.
* Takes into account media queries by recursing through child rules if they are present.
*/
function recursivelySetNamepace(compiled, namespace) {
	return compiled.map((rule) => {
		if (rule.type === "rule") {
			rule.value = `${namespace} ${rule.value}`;
			rule.value = rule.value.replaceAll(",", `,${namespace} `);
			rule.props = rule.props.map((prop) => {
				return `${namespace} ${prop}`;
			});
		}
		if (Array.isArray(rule.children) && rule.type !== "@keyframes") rule.children = recursivelySetNamepace(rule.children, namespace);
		return rule;
	});
}
function createStylisInstance({ options = EMPTY_OBJECT, plugins = EMPTY_ARRAY } = EMPTY_OBJECT) {
	let _componentId;
	let _selector;
	let _selectorRegexp;
	const selfReferenceReplacer = (match, offset, string) => {
		if (string.startsWith(_selector) && string.endsWith(_selector) && string.replaceAll(_selector, "").length > 0) return `.${_componentId}`;
		return match;
	};
	/**
	* When writing a style like
	*
	* & + & {
	*   color: red;
	* }
	*
	* The second ampersand should be a reference to the static component class. stylis
	* has no knowledge of static class so we have to intelligently replace the base selector.
	*
	* https://github.com/thysultan/stylis.js/tree/v4.0.2#abstract-syntax-structure
	*/
	const selfReferenceReplacementPlugin = (element) => {
		if (element.type === stylis$1.RULESET && element.value.includes("&")) element.props[0] = element.props[0].replace(AMP_REGEX, _selector).replace(_selectorRegexp, selfReferenceReplacer);
	};
	const middlewares = plugins.slice();
	middlewares.push(selfReferenceReplacementPlugin);
	/**
	* Enables automatic vendor-prefixing for styles.
	*/
	if (options.prefix) middlewares.push(stylis$1.prefixer);
	middlewares.push(stylis$1.stringify);
	const stringifyRules = (css$1, selector = "", prefix = "", componentId = "&") => {
		_componentId = componentId;
		_selector = selector;
		_selectorRegexp = new RegExp(`\\${_selector}\\b`, "g");
		const flatCSS = css$1.replace(COMMENT_REGEX, "");
		let compiled = stylis$1.compile(prefix || selector ? `${prefix} ${selector} { ${flatCSS} }` : flatCSS);
		if (options.namespace) compiled = recursivelySetNamepace(compiled, options.namespace);
		const stack = [];
		stylis$1.serialize(compiled, stylis$1.middleware(middlewares.concat(stylis$1.rulesheet((value) => stack.push(value)))));
		return stack;
	};
	stringifyRules.hash = plugins.length ? plugins.reduce((acc, plugin) => {
		if (!plugin.name) throwStyledComponentsError(15);
		return phash(acc, plugin.name);
	}, SEED$1).toString() : "";
	return stringifyRules;
}

//#endregion
//#region src/models/StyleSheetManager.tsx
const mainSheet = new StyleSheet();
const mainStylis = createStylisInstance();
const StyleSheetContext = createContext({
	shouldForwardProp: void 0,
	styleSheet: mainSheet,
	stylis: mainStylis
});
StyleSheetContext.displayName = "StyleSheetContext";
const StyleSheetConsumer = StyleSheetContext.Consumer;
const StylisContext = createContext(void 0);
StylisContext.displayName = "StylisContext";
const StylisConsumer = StylisContext.Consumer;
function useStyleSheetContext() {
	return useContext(StyleSheetContext);
}
function StyleSheetManager(props) {
	const $ = c(18);
	const [plugins, setPlugins] = useState(props.stylisPlugins);
	const { styleSheet } = useStyleSheetContext();
	let sheet;
	if ($[0] !== props.disableCSSOMInjection || $[1] !== props.target || $[2] !== styleSheet) {
		sheet = styleSheet;
		if (props.target) sheet = sheet.reconstructWithOptions({ target: props.target }, false);
		if (props.disableCSSOMInjection) sheet = sheet.reconstructWithOptions({ useCSSOMInjection: false });
		$[0] = props.disableCSSOMInjection;
		$[1] = props.target;
		$[2] = styleSheet;
		$[3] = sheet;
	} else sheet = $[3];
	const resolvedStyleSheet = sheet;
	let t0;
	if ($[4] !== plugins || $[5] !== props.enableVendorPrefixes || $[6] !== props.namespace) {
		t0 = createStylisInstance({
			options: {
				namespace: props.namespace,
				prefix: props.enableVendorPrefixes
			},
			plugins
		});
		$[4] = plugins;
		$[5] = props.enableVendorPrefixes;
		$[6] = props.namespace;
		$[7] = t0;
	} else t0 = $[7];
	const stylis$2 = t0;
	if (!shallowequal(plugins, props.stylisPlugins)) setPlugins(props.stylisPlugins);
	let t1;
	if ($[8] !== props.shouldForwardProp || $[9] !== resolvedStyleSheet || $[10] !== stylis$2) {
		t1 = {
			shouldForwardProp: props.shouldForwardProp,
			styleSheet: resolvedStyleSheet,
			stylis: stylis$2
		};
		$[8] = props.shouldForwardProp;
		$[9] = resolvedStyleSheet;
		$[10] = stylis$2;
		$[11] = t1;
	} else t1 = $[11];
	const styleSheetContextValue = t1;
	let t2;
	if ($[12] !== props.children || $[13] !== stylis$2) {
		t2 = /* @__PURE__ */ jsx(StylisContext, {
			value: stylis$2,
			children: props.children
		});
		$[12] = props.children;
		$[13] = stylis$2;
		$[14] = t2;
	} else t2 = $[14];
	let t3;
	if ($[15] !== styleSheetContextValue || $[16] !== t2) {
		t3 = /* @__PURE__ */ jsx(StyleSheetContext, {
			value: styleSheetContextValue,
			children: t2
		});
		$[15] = styleSheetContextValue;
		$[16] = t2;
		$[17] = t3;
	} else t3 = $[17];
	return t3;
}

//#endregion
//#region src/models/Keyframes.ts
var Keyframes = class {
	id;
	name;
	rules;
	constructor(name, rules) {
		this.name = name;
		this.id = `sc-keyframes-${name}`;
		this.rules = rules;
	}
	toString = () => {
		throw throwStyledComponentsError(12, String(this.name));
	};
	inject = (styleSheet, stylisInstance = mainStylis) => {
		const resolvedName = this.name + stylisInstance.hash;
		if (!styleSheet.hasNameForId(this.id, resolvedName)) styleSheet.insertRules(this.id, resolvedName, stylisInstance(this.rules, resolvedName, "@keyframes"));
	};
	getName(stylisInstance = mainStylis) {
		return this.name + stylisInstance.hash;
	}
};

//#endregion
//#region src/utils/addUnitIfNeeded.ts
function addUnitIfNeeded(name, value) {
	if (value == null || typeof value === "boolean" || value === "") return "";
	if (typeof value === "number" && value !== 0 && !(name in unitless) && !name.startsWith("--")) return `${value}px`;
	return String(value).trim();
}

//#endregion
//#region src/utils/hyphenateStyleName.ts
const isUpper = (c$1) => c$1 >= "A" && c$1 <= "Z";
/**
* Hyphenates a camelcased CSS property name, for example:
*
*   > hyphenateStyleName('backgroundColor')
*   < "background-color"
*   > hyphenateStyleName('MozTransition')
*   < "-moz-transition"
*   > hyphenateStyleName('msTransition')
*   < "-ms-transition"
*
* As Modernizr suggests (http://modernizr.com/docs/#prefixed), an `ms` prefix
* is converted to `-ms-`.
*/
function hyphenateStyleName(string) {
	let output = "";
	for (let i = 0; i < string.length; i++) {
		const c$1 = string[i];
		if (i === 1 && c$1 === "-" && string[0] === "-") return string;
		if (isUpper(c$1)) output += "-" + c$1.toLowerCase();
		else output += c$1;
	}
	return output.startsWith("ms-") ? "-" + output : output;
}

//#endregion
//#region src/utils/isStatelessFunction.ts
function isStatelessFunction(test) {
	return isFunction(test) && !(test.prototype && test.prototype.isReactComponent);
}

//#endregion
//#region src/utils/flatten.ts
/**
* It's falsish not falsy because 0 is allowed.
*/
const isFalsish = (chunk) => chunk === void 0 || chunk === null || chunk === false || chunk === "";
const objToCssArray = (obj) => {
	const rules = [];
	for (const key in obj) {
		const val = obj[key];
		if (!obj.hasOwnProperty(key) || isFalsish(val)) continue;
		if (Array.isArray(val) && val.isCss || isFunction(val)) rules.push(`${hyphenateStyleName(key)}:`, val, ";");
		else if (isPlainObject(val)) rules.push(`${key} {`, ...objToCssArray(val), "}");
		else rules.push(`${hyphenateStyleName(key)}: ${addUnitIfNeeded(key, val)};`);
	}
	return rules;
};
function flatten(chunk, executionContext, styleSheet, stylisInstance) {
	if (isFalsish(chunk)) return [];
	if (isStyledComponent(chunk)) return [`.${chunk.styledComponentId}`];
	if (isFunction(chunk)) if (isStatelessFunction(chunk) && executionContext) {
		const result = chunk(executionContext);
		if (process.env.NODE_ENV !== "production" && typeof result === "object" && !Array.isArray(result) && !(result instanceof Keyframes) && !isPlainObject(result) && result !== null) console.error(`${getComponentName(chunk)} is not a styled component and cannot be referred to via component selector. See https://www.styled-components.com/docs/advanced#referring-to-other-components for more details.`);
		return flatten(result, executionContext, styleSheet, stylisInstance);
	} else return [chunk];
	if (chunk instanceof Keyframes) if (styleSheet) {
		chunk.inject(styleSheet, stylisInstance);
		return [chunk.getName(stylisInstance)];
	} else return [chunk];
	if (isPlainObject(chunk)) return objToCssArray(chunk);
	if (!Array.isArray(chunk)) return [chunk.toString()];
	return chunk.flatMap((chunklet) => flatten(chunklet, executionContext, styleSheet, stylisInstance));
}

//#endregion
//#region src/utils/isStaticRules.ts
function isStaticRules(rules) {
	for (let i = 0; i < rules.length; i += 1) {
		const rule = rules[i];
		if (isFunction(rule) && !isStyledComponent(rule)) return false;
	}
	return true;
}

//#endregion
//#region src/models/ComponentStyle.ts
const SEED = hash$1(SC_VERSION);
/**
* ComponentStyle is all the CSS-specific stuff, not the React-specific stuff.
*/
var ComponentStyle = class {
	baseHash;
	baseStyle;
	componentId;
	isStatic;
	rules;
	staticRulesId = "";
	buffer = [];
	constructor(rules, componentId, baseStyle) {
		this.rules = rules;
		this.isStatic = process.env.NODE_ENV === "production" && (baseStyle === void 0 || baseStyle.isStatic) && isStaticRules(rules);
		this.componentId = componentId;
		this.baseHash = phash(SEED, componentId);
		this.baseStyle = baseStyle;
		StyleSheet.registerId(componentId);
	}
	generateStyles(executionContext, styleSheet, stylis$2) {
		let className = this.baseStyle?.generateStyles(executionContext, styleSheet, stylis$2) ?? "";
		if (this.isStatic && !stylis$2.hash) if (this.staticRulesId && styleSheet.hasNameForId(this.componentId, this.staticRulesId)) className = joinStrings(className, this.staticRulesId);
		else {
			const cssStatic = joinStringArray(flatten(this.rules, executionContext, styleSheet, stylis$2));
			const name = generateAlphabeticName(phash(this.baseHash, cssStatic) >>> 0);
			if (!styleSheet.hasNameForId(this.componentId, name)) {
				const cssStaticFormatted = stylis$2(cssStatic, `.${name}`, void 0, this.componentId);
				this.buffer.push([name, cssStaticFormatted]);
			}
			className = joinStrings(className, name);
			this.staticRulesId = name;
		}
		else {
			let dynamicHash = phash(this.baseHash, stylis$2.hash);
			let css$1 = "";
			for (let i = 0; i < this.rules.length; i++) {
				const partRule = this.rules[i];
				if (typeof partRule === "string") {
					css$1 += partRule;
					if (process.env.NODE_ENV !== "production") dynamicHash = phash(dynamicHash, partRule);
				} else if (partRule) {
					const partString = joinStringArray(flatten(partRule, executionContext, styleSheet, stylis$2));
					dynamicHash = phash(dynamicHash, partString + i);
					css$1 += partString;
				}
			}
			if (css$1) {
				const name = generateAlphabeticName(dynamicHash >>> 0);
				if (!styleSheet.hasNameForId(this.componentId, name)) {
					const cssFormatted = stylis$2(css$1, `.${name}`, void 0, this.componentId);
					this.buffer.push([name, cssFormatted]);
				}
				className = joinStrings(className, name);
			}
		}
		return className;
	}
	flushStyles(styleSheet) {
		this.baseStyle?.flushStyles(styleSheet);
		for (const [name, rules] of this.buffer) if (!styleSheet.hasNameForId(this.componentId, name)) styleSheet.insertRules(this.componentId, name, rules);
		this.buffer.length = 0;
	}
};

//#endregion
//#region src/models/ThemeProvider.tsx
const ThemeContext = createContext(void 0);
ThemeContext.displayName = "ThemeContext";
function mergeTheme(theme, outerTheme) {
	if (!theme) throw throwStyledComponentsError(14);
	if (isFunction(theme)) {
		const mergedTheme = theme(outerTheme);
		if (process.env.NODE_ENV !== "production" && (mergedTheme === null || Array.isArray(mergedTheme) || typeof mergedTheme !== "object")) throw throwStyledComponentsError(7);
		return mergedTheme;
	}
	if (Array.isArray(theme) || typeof theme !== "object") throw throwStyledComponentsError(8);
	return outerTheme ? {
		...outerTheme,
		...theme
	} : theme;
}
/**
* Returns the current theme (as provided by the closest ancestor `ThemeProvider`.)
*
* If no `ThemeProvider` is found, the function will error. If you need access to the theme in an
* uncertain composition scenario, `React.useContext(ThemeContext)` will not emit an error if there
* is no `ThemeProvider` ancestor.
*/
function useTheme() {
	const theme = useContext(ThemeContext);
	if (!theme) throw throwStyledComponentsError(18);
	return theme;
}
/**
* Provide a theme to an entire react component tree via context
*/
function ThemeProvider(props) {
	const $ = c(6);
	if (!props.children) return null;
	const t0 = props.theme;
	const t1 = use(ThemeContext);
	let t2;
	if ($[0] !== props.theme || $[1] !== t1) {
		t2 = mergeTheme(t0, t1);
		$[0] = props.theme;
		$[1] = t1;
		$[2] = t2;
	} else t2 = $[2];
	const theme = t2;
	let t3;
	if ($[3] !== props.children || $[4] !== theme) {
		t3 = /* @__PURE__ */ jsx(ThemeContext, {
			value: theme,
			children: props.children
		});
		$[3] = props.children;
		$[4] = theme;
		$[5] = t3;
	} else t3 = $[5];
	return t3;
}

//#endregion
//#region src/utils/useHydrating.ts
function useHydrating() {
	return useSyncExternalStore(noop, getSnapshot, getServerSnapshot);
}
function noop() {
	return () => {};
}
function getSnapshot() {
	return false;
}
function getServerSnapshot() {
	return true;
}

//#endregion
//#region src/models/StyledComponent.tsx
const identifiers = {};
function generateId(displayName, parentComponentId) {
	const name = typeof displayName !== "string" ? "sc" : escape(displayName);
	identifiers[name] = (identifiers[name] || 0) + 1;
	const componentId = `${name}-${generateComponentId(SC_VERSION + name + identifiers[name])}`;
	return parentComponentId ? `${parentComponentId}-${componentId}` : componentId;
}
function useStyles(componentStyle, stylis$2, styleSheet, resolvedAttrs) {
	const $ = c(5);
	let t0;
	if ($[0] !== componentStyle || $[1] !== resolvedAttrs || $[2] !== styleSheet || $[3] !== stylis$2) {
		t0 = componentStyle.generateStyles(resolvedAttrs, styleSheet, stylis$2);
		$[0] = componentStyle;
		$[1] = resolvedAttrs;
		$[2] = styleSheet;
		$[3] = stylis$2;
		$[4] = t0;
	} else t0 = $[4];
	const className = t0;
	useDebugValue(className, _temp$1);
	return className;
}
function _temp$1(value) {
	return `className: ${value}`;
}
function resolveContext(attrs, props, theme) {
	const context = {
		...props,
		className: void 0,
		theme
	};
	let attrDef;
	for (let i = 0; i < attrs.length; i += 1) {
		attrDef = attrs[i];
		const resolvedAttrDef = isFunction(attrDef) ? attrDef(context) : attrDef;
		for (const key in resolvedAttrDef) context[key] = key === "className" ? joinStrings(context[key], resolvedAttrDef[key]) : key === "style" ? {
			...context[key],
			...resolvedAttrDef[key]
		} : resolvedAttrDef[key];
	}
	if (props.className) context.className = joinStrings(context.className, props.className);
	return context;
}
let seenUnknownProps = /* @__PURE__ */ new Set();
function useStyledComponent(forwardedComponent, props) {
	const $ = c(13);
	const { attrs: componentAttrs, componentStyle, defaultProps, foldedComponentIds, styledComponentId, target } = forwardedComponent;
	const contextTheme = componentStyle.isStatic ? EMPTY_OBJECT : use(ThemeContext);
	const ssc = useStyleSheetContext();
	const shouldForwardProp = forwardedComponent.shouldForwardProp || ssc.shouldForwardProp;
	useDebugValue(styledComponentId, _temp2);
	let t0;
	let theme;
	if ($[0] !== componentAttrs || $[1] !== contextTheme || $[2] !== defaultProps || $[3] !== props) {
		theme = determineTheme(props, contextTheme, defaultProps) || EMPTY_OBJECT;
		t0 = resolveContext(componentAttrs, props, theme);
		$[0] = componentAttrs;
		$[1] = contextTheme;
		$[2] = defaultProps;
		$[3] = props;
		$[4] = t0;
		$[5] = theme;
	} else {
		t0 = $[4];
		theme = $[5];
	}
	const context = t0;
	const ElementToBeCreated = context.as || target;
	const propsForElement = {};
	for (const key in context) if (context[key] === void 0) {} else if (key[0] === "$" || key === "as" || key === "theme" && context.theme === theme) {} else if (key === "forwardedAs") propsForElement.as = context.forwardedAs;
	else if (!shouldForwardProp || shouldForwardProp(key, ElementToBeCreated)) {
		propsForElement[key] = context[key];
		if (!shouldForwardProp && process.env.NODE_ENV === "development" && !isPropValid(key) && !seenUnknownProps.has(key) && domElements_default.has(ElementToBeCreated)) {
			seenUnknownProps.add(key);
			console.warn(`styled-components: it looks like an unknown prop "${key}" is being sent through to the DOM, which will likely trigger a React console error. If you would like automatic filtering of unknown props, you can opt-into that behavior via \`<StyleSheetManager shouldForwardProp={...}>\` (connect an API like \`@emotion/is-prop-valid\`) or consider using transient props (\`$\` prefix for automatic filtering.)`);
		}
	}
	const isHydrating = useHydrating();
	let t1;
	if ($[6] !== isHydrating || $[7] !== ssc.styleSheet) {
		t1 = isHydrating ? new StyleSheet({ isServer: true }) : ssc.styleSheet;
		$[6] = isHydrating;
		$[7] = ssc.styleSheet;
		$[8] = t1;
	} else t1 = $[8];
	const styleSheet = t1;
	const generatedClassName = useStyles(componentStyle, ssc.stylis, styleSheet, context);
	if (process.env.NODE_ENV !== "production" && forwardedComponent.warnTooManyClasses) forwardedComponent.warnTooManyClasses(generatedClassName);
	let classString = joinStrings(foldedComponentIds, styledComponentId);
	if (generatedClassName) classString = classString + (" " + generatedClassName);
	if (context.className) classString = classString + (" " + context.className);
	propsForElement[isTag(ElementToBeCreated) && !domElements_default.has(ElementToBeCreated) ? "class" : "className"] = classString;
	let t2;
	if ($[9] !== componentStyle || $[10] !== isHydrating || $[11] !== ssc.styleSheet) {
		t2 = () => {
			if (!isHydrating) componentStyle.flushStyles(ssc.styleSheet);
		};
		$[9] = componentStyle;
		$[10] = isHydrating;
		$[11] = ssc.styleSheet;
		$[12] = t2;
	} else t2 = $[12];
	useInsertionEffect(t2);
	const children = /* @__PURE__ */ jsx(ElementToBeCreated, { ...propsForElement });
	if (isHydrating) {
		componentStyle.flushStyles(styleSheet);
		const css$1 = outputSheetModern(styleSheet);
		return /* @__PURE__ */ jsxs(Fragment, { children: [children, css$1.map(_temp3)] });
	}
	return children;
}
function _temp3(t0) {
	const [id, cssRules] = t0;
	return /* @__PURE__ */ jsx("style", {
		href: hash(cssRules),
		precedence: "sc",
		children: cssRules
	}, id);
}
function _temp2(value) {
	return `styledComponentId: ${value}`;
}
const outputSheetModern = (sheet) => {
	const tag = sheet.getTag();
	const { length } = tag;
	let css$1 = [];
	for (let group = 0; group < length; group++) {
		const id = getIdForGroup(group);
		if (id === void 0) continue;
		const rules = tag.getGroup(group);
		if (rules.length === 0) continue;
		css$1.push([id, rules]);
	}
	return css$1;
};
function createStyledComponent(target, options, rules) {
	const isTargetStyledComp = isStyledComponent(target);
	const styledComponentTarget = target;
	const isCompositeComponent = !isTag(target);
	const { attrs = EMPTY_ARRAY, componentId = generateId(options.displayName, options.parentComponentId), displayName = generateDisplayName(target) } = options;
	const styledComponentId = options.displayName && options.componentId ? `${escape(options.displayName)}-${options.componentId}` : options.componentId || componentId;
	const finalAttrs = isTargetStyledComp && styledComponentTarget.attrs ? styledComponentTarget.attrs.concat(attrs).filter(Boolean) : attrs;
	let { shouldForwardProp } = options;
	if (isTargetStyledComp && styledComponentTarget.shouldForwardProp) {
		const shouldForwardPropFn = styledComponentTarget.shouldForwardProp;
		if (options.shouldForwardProp) {
			const passedShouldForwardPropFn = options.shouldForwardProp;
			shouldForwardProp = (prop, elementToBeCreated) => shouldForwardPropFn(prop, elementToBeCreated) && passedShouldForwardPropFn(prop, elementToBeCreated);
		} else shouldForwardProp = shouldForwardPropFn;
	}
	const componentStyle = new ComponentStyle(rules, styledComponentId, isTargetStyledComp ? styledComponentTarget.componentStyle : void 0);
	function render(props) {
		return useStyledComponent(WrappedStyledComponent, props);
	}
	render.displayName = displayName;
	/**
	* memo creates a new interim component, which we'll take advantage of
	* instead of extending ParentComponent to create _another_ interim class
	*/
	let WrappedStyledComponent = memo(render);
	WrappedStyledComponent.attrs = finalAttrs;
	WrappedStyledComponent.componentStyle = componentStyle;
	WrappedStyledComponent.displayName = displayName;
	WrappedStyledComponent.shouldForwardProp = shouldForwardProp;
	WrappedStyledComponent.foldedComponentIds = isTargetStyledComp ? joinStrings(styledComponentTarget.foldedComponentIds, styledComponentTarget.styledComponentId) : "";
	WrappedStyledComponent.styledComponentId = styledComponentId;
	WrappedStyledComponent.target = isTargetStyledComp ? styledComponentTarget.target : target;
	Object.defineProperty(WrappedStyledComponent, "defaultProps", {
		get() {
			return this._foldedDefaultProps;
		},
		set(obj) {
			this._foldedDefaultProps = isTargetStyledComp ? mixinDeep({}, styledComponentTarget.defaultProps, obj) : obj;
		}
	});
	if (process.env.NODE_ENV !== "production") {
		checkDynamicCreation(displayName, styledComponentId);
		WrappedStyledComponent.warnTooManyClasses = createWarnTooManyClasses_default(displayName, styledComponentId);
	}
	Object.defineProperty(WrappedStyledComponent, "toString", { value: () => `.${WrappedStyledComponent.styledComponentId}` });
	if (isCompositeComponent) hoistNonReactStatics(WrappedStyledComponent, target, {
		attrs: true,
		componentStyle: true,
		displayName: true,
		foldedComponentIds: true,
		shouldForwardProp: true,
		styledComponentId: true,
		target: true
	});
	return WrappedStyledComponent;
}
var StyledComponent_default = createStyledComponent;
/** Hash a string using the djb2 algorithm. */
function hash(value) {
	let h = 5381;
	for (let index = 0, len = value.length; index < len; index++) h = (h << 5) + h + value.charCodeAt(index) >>> 0;
	return h.toString(36);
}

//#endregion
//#region src/utils/interleave.ts
function interleave(strings, interpolations) {
	const result = [strings[0]];
	for (let i = 0, len = interpolations.length; i < len; i += 1) result.push(interpolations[i], strings[i + 1]);
	return result;
}

//#endregion
//#region src/constructors/css.ts
/**
* Used when flattening object styles to determine if we should
* expand an array of styles.
*/
const addTag = (arg) => Object.assign(arg, { isCss: true });
function css(styles, ...interpolations) {
	if (isFunction(styles) || isPlainObject(styles)) return addTag(flatten(interleave(EMPTY_ARRAY, [styles, ...interpolations])));
	const styleStringArray = styles;
	if (interpolations.length === 0 && styleStringArray.length === 1 && typeof styleStringArray[0] === "string") return flatten(styleStringArray);
	return addTag(flatten(interleave(styleStringArray, interpolations)));
}
var css_default = css;

//#endregion
//#region src/constructors/constructWithOptions.ts
function constructWithOptions(componentConstructor, tag, options = EMPTY_OBJECT) {
	/**
	* We trust that the tag is a valid component as long as it isn't
	* falsish. Typically the tag here is a string or function (i.e.
	* class or pure function component), however a component may also be
	* an object if it uses another utility, e.g. React.memo. React will
	* output an appropriate warning however if the `tag` isn't valid.
	*/
	if (!tag) throw throwStyledComponentsError(1, tag);
	const templateFunction = (initialStyles, ...interpolations) => componentConstructor(tag, options, css_default(initialStyles, ...interpolations));
	/**
	* Attrs allows for accomplishing two goals:
	*
	* 1. Backfilling props at runtime more expressively than defaultProps
	* 2. Amending the prop interface of a wrapped styled component
	*/
	templateFunction.attrs = (attrs) => constructWithOptions(componentConstructor, tag, {
		...options,
		attrs: Array.prototype.concat(options.attrs, attrs).filter(Boolean)
	});
	/**
	* If config methods are called, wrap up a new template function
	* and merge options.
	*/
	templateFunction.withConfig = (config) => constructWithOptions(componentConstructor, tag, {
		...options,
		...config,
		componentId: options.componentId || config.componentId,
		displayName: options.displayName || config.displayName
	});
	return templateFunction;
}

//#endregion
//#region src/constructors/styled.tsx
const baseStyled = (tag) => constructWithOptions(StyledComponent_default, tag);
const styled = baseStyled;
domElements_default.forEach((domElement) => {
	styled[domElement] = baseStyled(domElement);
});
var styled_default = styled;

//#endregion
//#region src/models/GlobalStyle.ts
var GlobalStyle = class {
	componentId;
	isStatic;
	rules;
	constructor(rules, componentId) {
		this.rules = rules;
		this.componentId = componentId;
		this.isStatic = isStaticRules(rules);
		StyleSheet.registerId(this.componentId + 1);
	}
	createStyles(instance, executionContext, styleSheet, stylis$2) {
		const flatCSS = joinStringArray(flatten(this.rules, executionContext, styleSheet, stylis$2));
		const css$1 = stylis$2(flatCSS, "");
		const id = this.componentId + instance;
		styleSheet.insertRules(id, id, css$1);
	}
	removeStyles(instance, styleSheet) {
		styleSheet.clearRules(this.componentId + instance);
	}
	renderStyles(instance, executionContext, styleSheet, stylis$2) {
		if (instance > 2) StyleSheet.registerId(this.componentId + instance);
		this.removeStyles(instance, styleSheet);
		this.createStyles(instance, executionContext, styleSheet, stylis$2);
	}
};

//#endregion
//#region src/constructors/createGlobalStyle.ts
function createGlobalStyle(strings, ...interpolations) {
	const rules = css_default(strings, ...interpolations);
	const styledComponentId = `sc-global-${generateComponentId(JSON.stringify(rules))}`;
	const globalStyle = new GlobalStyle(rules, styledComponentId);
	if (process.env.NODE_ENV !== "production") checkDynamicCreation(styledComponentId);
	const GlobalStyleComponent = (props) => {
		const $ = c(9);
		const ssc = useStyleSheetContext();
		const theme = useContext(ThemeContext);
		let t0;
		if ($[0] !== ssc.styleSheet) {
			t0 = () => ssc.styleSheet.allocateGSInstance(styledComponentId);
			$[0] = ssc.styleSheet;
			$[1] = t0;
		} else t0 = $[1];
		const [instance] = useState(t0);
		if (process.env.NODE_ENV !== "production" && Children.count(props.children)) console.warn(`The global style component ${styledComponentId} was given child JSX. createGlobalStyle does not render children.`);
		if (process.env.NODE_ENV !== "production" && rules.some(_temp)) console.warn("Please do not use @import CSS syntax in createGlobalStyle at this time, as the CSSOM APIs we use in production do not handle it well. Instead, we recommend using a library such as react-helmet to inject a typical <link> meta tag to the stylesheet, or simply embedding it manually in your index.html <head> section for a simpler app.");
		if (useHydrating()) throw new TypeError("createGlobalStyle does not support SSR, use hydration check to detect it");
		let t1;
		let t2;
		if ($[2] !== instance || $[3] !== props || $[4] !== ssc.styleSheet || $[5] !== ssc.stylis || $[6] !== theme) {
			t1 = () => {
				renderStyles(instance, props, ssc.styleSheet, theme, ssc.stylis);
				return () => {
					globalStyle.removeStyles(instance, ssc.styleSheet);
				};
			};
			t2 = [
				instance,
				props,
				ssc.styleSheet,
				theme,
				ssc.stylis
			];
			$[2] = instance;
			$[3] = props;
			$[4] = ssc.styleSheet;
			$[5] = ssc.stylis;
			$[6] = theme;
			$[7] = t1;
			$[8] = t2;
		} else {
			t1 = $[7];
			t2 = $[8];
		}
		useInsertionEffect(t1, t2);
		return null;
	};
	function renderStyles(instance, props, styleSheet, theme, stylis$2) {
		if (globalStyle.isStatic) globalStyle.renderStyles(instance, STATIC_EXECUTION_CONTEXT, styleSheet, stylis$2);
		else {
			const context = {
				...props,
				theme: determineTheme(props, theme, GlobalStyleComponent.defaultProps)
			};
			globalStyle.renderStyles(instance, context, styleSheet, stylis$2);
		}
	}
	return memo(GlobalStyleComponent);
}
function _temp(rule) {
	return typeof rule === "string" && rule.indexOf("@import") !== -1;
}

//#endregion
//#region src/constructors/keyframes.ts
function keyframes(strings, ...interpolations) {
	if (process.env.NODE_ENV !== "production" && typeof navigator !== "undefined" && navigator.product === "ReactNative") console.warn("`keyframes` cannot be used on ReactNative, only on the web. To do animation in ReactNative please use Animated.");
	const rules = joinStringArray(css_default(strings, ...interpolations));
	const name = generateComponentId(rules);
	return new Keyframes(name, rules);
}

//#endregion
//#region src/secretInternals.ts
const __PRIVATE__ = {
	StyleSheet,
	mainSheet
};

//#endregion
//#region src/index.ts
if (process.env.NODE_ENV !== "production" && typeof navigator !== "undefined" && navigator.product === "ReactNative") console.warn(`It looks like you've imported 'styled-components' on React Native.\nPerhaps you're looking to import 'styled-components/native'?\nRead more about this at https://www.styled-components.com/docs/basics#react-native`);
const windowGlobalKey = `__sc-${SC_ATTR}__`;
if (process.env.NODE_ENV !== "production" && process.env.NODE_ENV !== "test" && typeof window !== "undefined") {
	window[windowGlobalKey] ||= 0;
	if (window[windowGlobalKey] === 1) console.warn(`It looks like there are several instances of 'styled-components' initialized in this application. This may cause dynamic styles to not render properly, errors during the rehydration process, a missing theme prop, and makes your application bigger without good reason.\n\nSee https://s-c.sh/2BAXzed for more info.`);
	window[windowGlobalKey] += 1;
}
var src_default = styled_default;

//#endregion
export { StyleSheetConsumer, StyleSheetContext, StyleSheetManager, ThemeContext, ThemeProvider, __PRIVATE__, createGlobalStyle, css_default as css, src_default as default, isStyledComponent, keyframes, styled_default as styled, useTheme, SC_VERSION as version };
//# sourceMappingURL=index.js.map